import { useEffect, useState } from "react";
import { toast } from "sonner";
import { User, Mail, Phone, Key, Building2, Home, Hash, MapPin, Receipt, Monitor, Trash2, ShieldOff, Calendar, Copy, Check } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * ClientProfile — host self-service profile editor.
 * Personal name, phone, password and the new billing/fiscal block:
 * company name, accommodation name, NIF (PT 9 digits) and billing address.
 * These four are optional but, when filled, are surfaced on every PDF
 * invoice issued by LuxStay (used for IRS / IVA purposes).
 */
export default function ClientProfile() {
  const { user, refreshMe } = useAuth();
  const [form, setForm] = useState({
    name: "", phone: "",
    company_name: "", accommodation_name: "", nif: "", billing_address: "",
    new_password: "", confirm_password: "",
  });
  const [busy, setBusy] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  async function loadSessions() {
    setLoadingSessions(true);
    try {
      const { data } = await api.get("/auth/sessions");
      setSessions(data.items || []);
    } catch (_) { /* user without sessions yet — fine */ }
    finally { setLoadingSessions(false); }
  }
  useEffect(() => { loadSessions(); }, []);

  async function revoke(sid) {
    try {
      await api.delete(`/auth/sessions/${sid}`);
      toast.success("Sessão terminada.");
      loadSessions();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  async function revokeAll() {
    if (!window.confirm("Terminar sessão em todos os dispositivos? Vai precisar de iniciar sessão novamente em cada um.")) return;
    try {
      const { data } = await api.post("/auth/sessions/revoke-all");
      toast.success(`${data.revoked} sessões terminadas.`);
      loadSessions();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  useEffect(() => {
    if (user) {
      setForm(f => ({
        ...f,
        name: user.name || "",
        phone: user.phone || "",
        company_name: user.company_name || "",
        accommodation_name: user.accommodation_name || "",
        nif: user.nif || "",
        billing_address: user.billing_address || "",
      }));
    }
  }, [user]);

  function nifIsValid(raw) {
    if (!raw) return true; // empty allowed
    const digits = String(raw).replace(/\D/g, "");
    return digits.length === 9;
  }

  async function save(e) {
    e.preventDefault();
    if (form.new_password && form.new_password !== form.confirm_password) {
      toast.error("As palavras-passe não coincidem.");
      return;
    }
    if (form.new_password && form.new_password.length < 6) {
      toast.error("A palavra-passe deve ter pelo menos 6 caracteres.");
      return;
    }
    if (!nifIsValid(form.nif)) {
      toast.error("NIF inválido — deve ter 9 dígitos.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        company_name: form.company_name,
        accommodation_name: form.accommodation_name,
        nif: form.nif,
        billing_address: form.billing_address,
      };
      if (form.new_password) payload.password = form.new_password;
      await api.put("/auth/me", payload);
      toast.success("Perfil atualizado.");
      setForm(f => ({ ...f, new_password: "", confirm_password: "" }));
      refreshMe?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  }

  if (!user) return null;

  return (
    <div className="space-y-10 max-w-3xl" data-testid="client-profile">
      <div>
        <div className="eyebrow mb-3">Dados pessoais e fiscais</div>
        <h1 className="font-display text-5xl">O meu perfil.</h1>
        <div className="text-sm text-[#a0b2af] mt-2">
          Atualize os seus dados de contacto e os dados fiscais que aparecem
          nas faturas (NIF, nome de empresa, alojamento e morada).
        </div>
      </div>

      {/* Identity strip */}
      <div className="lux-card p-6 flex items-center gap-5 flex-wrap">
        <div className="w-16 h-16 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/40 grid place-items-center">
          <User size={28} className="text-[#d4af37]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-3xl">{user.company_name || user.name}</div>
          {user.company_name && user.name && user.company_name !== user.name && (
            <div className="text-xs text-[#a0b2af] mt-1">A/C {user.name}</div>
          )}
          <div className="text-xs text-[#a0b2af] mt-1">
            <Mail size={10} className="inline mr-1" /> {user.email}
          </div>
          {user.nif && (
            <div className="text-[10px] uppercase tracking-widest mt-2 text-[#d4af37]">
              <Hash size={10} className="inline mr-1" /> NIF {user.nif}
            </div>
          )}
        </div>
      </div>

      {/* Edit form */}
      <form onSubmit={save} className="lux-card p-6 space-y-6" data-testid="client-profile-form">

        {/* SECTION 1 — Personal */}
        <div className="space-y-5">
          <div className="eyebrow">Dados pessoais</div>
          <Field icon={<User size={14} />} label="Nome pessoal" value={form.name}
                 onChange={v => setForm(f => ({ ...f, name: v }))}
                 testId="client-profile-name" required />
          <Field icon={<Phone size={14} />} label="Telemóvel" value={form.phone} type="tel"
                 onChange={v => setForm(f => ({ ...f, phone: v }))}
                 testId="client-profile-phone" />
          <Field icon={<Mail size={14} />} label="Email (apenas leitura — contacte admin)"
                 value={user.email} disabled />
        </div>

        {/* SECTION 2 — Billing / fiscal */}
        <div className="pt-4 border-t border-[#1a4640] space-y-5">
          <div>
            <div className="eyebrow flex items-center gap-2">
              <Receipt size={12} className="text-[#d4af37]" /> Dados de faturação
            </div>
            <div className="text-[11px] text-[#a0b2af] mt-1.5">
              Opcionais. Se preencher, aparecem nas suas faturas — útil para IRS / IVA.
            </div>
          </div>
          <Field icon={<Building2 size={14} />} label="Nome da empresa (opcional)"
                 value={form.company_name}
                 onChange={v => setForm(f => ({ ...f, company_name: v }))}
                 placeholder="Ex.: Madeira Stay, Lda."
                 testId="client-profile-company" />
          <Field icon={<Home size={14} />} label="Nome do alojamento (opcional)"
                 value={form.accommodation_name}
                 onChange={v => setForm(f => ({ ...f, accommodation_name: v }))}
                 placeholder="Ex.: Casa do Mar — Funchal"
                 testId="client-profile-accommodation" />
          <Field icon={<Hash size={14} />} label="NIF (opcional, 9 dígitos)"
                 value={form.nif}
                 onChange={v => setForm(f => ({ ...f, nif: v.replace(/\D/g, "").slice(0, 9) }))}
                 placeholder="123456789"
                 testId="client-profile-nif"
                 inputMode="numeric"
                 maxLength={9} />
          <Field icon={<MapPin size={14} />} label="Morada de faturação (opcional)"
                 value={form.billing_address}
                 onChange={v => setForm(f => ({ ...f, billing_address: v }))}
                 placeholder="Rua, n.º, código-postal, localidade"
                 testId="client-profile-address" />
        </div>

        {/* SECTION 3 — Password */}
        <div className="pt-4 border-t border-[#1a4640] space-y-5">
          <div className="eyebrow text-[10px]">Alterar palavra-passe</div>
          <Field icon={<Key size={14} />} label="Nova palavra-passe (mín. 6 caracteres)"
                 value={form.new_password} type="password" placeholder="Deixe vazio para manter"
                 onChange={v => setForm(f => ({ ...f, new_password: v }))}
                 testId="client-profile-password" />
          {form.new_password && (
            <Field icon={<Key size={14} />} label="Confirmar nova palavra-passe"
                   value={form.confirm_password} type="password"
                   onChange={v => setForm(f => ({ ...f, confirm_password: v }))}
                   testId="client-profile-password-confirm" />
          )}
        </div>

        <button type="submit" disabled={busy}
                className="btn-gold px-6 py-3 text-sm disabled:opacity-60"
                data-testid="client-profile-save">
          {busy ? "A guardar…" : "Guardar alterações"}
        </button>
      </form>

      {/* Active sessions panel */}
      <div className="lux-card p-6 space-y-5" data-testid="client-sessions-panel">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="eyebrow flex items-center gap-2">
              <Monitor size={12} className="text-[#d4af37]" /> Sessões ativas
            </div>
            <div className="text-[11px] text-[#a0b2af] mt-1.5 max-w-md">
              Dispositivos onde optou por <span className="text-[#d4af37]">manter sessão ativa</span>.
              Pode terminar individualmente ou todas de uma vez.
            </div>
          </div>
          {sessions.length > 1 && (
            <button onClick={revokeAll}
                    className="btn-ghost-gold px-3 py-1.5 text-[11px] flex items-center gap-1.5"
                    data-testid="client-sessions-revoke-all">
              <ShieldOff size={12} /> Terminar todas
            </button>
          )}
        </div>

        {loadingSessions ? (
          <div className="text-xs text-[#a0b2af]">A carregar…</div>
        ) : sessions.length === 0 ? (
          <div className="text-xs text-[#a0b2af] italic">
            Nenhuma sessão persistente. Da próxima vez que entrar, marque
            «Manter sessão ativa» para ficar autenticado durante 30 dias.
          </div>
        ) : (
          <ul className="space-y-2.5" data-testid="client-sessions-list">
            {sessions.map(s => (
              <li key={s.id}
                  className="flex items-center justify-between gap-3 border border-[#1a4640] bg-[#031c18] px-4 py-3 rounded-sm"
                  data-testid={`session-row-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm flex items-center gap-2 flex-wrap">
                    <span className="truncate">{prettyUA(s.user_agent)}</span>
                    {s.current && (
                      <span className="text-[10px] uppercase tracking-widest text-[#10b981] border border-[#10b981]/40 px-1.5 py-0.5 rounded-sm">
                        Atual
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-[#a0b2af] mt-1">
                    {s.ip && <>IP {s.ip} · </>}
                    Última utilização {fmtAgo(s.last_used_at)} · Expira {fmtAgo(s.expires_at)}
                  </div>
                </div>
                <button onClick={() => revoke(s.id)}
                        className="text-[#a0b2af] hover:text-red-400 p-2"
                        title={s.current ? "Terminar esta sessão (vai sair)" : "Terminar"}
                        data-testid={`session-revoke-${s.id}`}>
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <CalendarSubscribe />
    </div>
  );
}

function CalendarSubscribe() {
  const [url, setUrl] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/client/calendar-token");
        if (!cancelled) setUrl(data.url || "");
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  function copy() {
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      toast.success("Link copiado para a área de transferência.");
      setTimeout(() => setCopied(false), 2500);
    }).catch(() => toast.error("Não foi possível copiar."));
  }

  return (
    <div className="lux-card p-6 space-y-5" data-testid="client-calendar-subscribe">
      <div>
        <div className="eyebrow flex items-center gap-2">
          <Calendar size={12} className="text-[#d4af37]" /> Subscrever o seu calendário
        </div>
        <p className="text-[11px] text-[#a0b2af] mt-1.5 max-w-md">
          Importe o link abaixo no Google, Apple ou Outlook Calendar para ver
          todas as suas limpezas (próximas e dos últimos 60 dias) sincronizadas
          automaticamente.
        </p>
      </div>

      <div className="flex items-stretch gap-2 flex-wrap">
        <input readOnly value={url || "A gerar…"}
               className="flex-1 min-w-[260px] bg-[#031c18] border border-[#1a4640] rounded-sm px-3 py-2.5 text-xs font-mono text-[#a0b2af]"
               data-testid="client-calendar-url"
               onFocus={e => e.target.select()} />
        <button onClick={copy} disabled={!url}
                className="btn-gold px-4 py-2.5 text-xs flex items-center gap-2 disabled:opacity-40"
                data-testid="client-calendar-copy">
          {copied ? <><Check size={12} /> Copiado</> : <><Copy size={12} /> Copiar</>}
        </button>
      </div>

      <div className="text-[10px] text-[#a0b2af] leading-relaxed">
        <strong className="text-[#fdfbf7]">Como subscrever:</strong>
        <ul className="list-disc list-inside mt-1 space-y-0.5">
          <li>Google Calendar → ⚙️ Definições → Adicionar calendário → Por URL.</li>
          <li>Apple Calendar (Mac) → Ficheiro → Nova subscrição de calendário.</li>
          <li>Outlook → Adicionar calendário → Subscrever da Web.</li>
        </ul>
      </div>
    </div>
  );
}

function Field({ icon, label, value, onChange, type = "text", testId,
                 required = false, disabled = false, placeholder = "",
                 inputMode, maxLength }) {
  return (
    <div className="space-y-2">
      <label className="text-xs uppercase tracking-widest text-[#a0b2af] flex items-center gap-2">
        <span className="text-[#d4af37]">{icon}</span>{label}
      </label>
      <input
        type={type}
        value={value || ""}
        onChange={e => onChange?.(e.target.value)}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        inputMode={inputMode}
        maxLength={maxLength}
        className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3 outline-none focus:border-[#d4af37] disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid={testId}
      />
    </div>
  );
}

function prettyUA(ua = "") {
  // Cheap UA labelling — good enough to recognise own devices in the list.
  if (!ua) return "Dispositivo desconhecido";
  const lower = ua.toLowerCase();
  let device = "Browser";
  if (/iphone/.test(lower)) device = "iPhone";
  else if (/ipad/.test(lower)) device = "iPad";
  else if (/android/.test(lower)) device = "Android";
  else if (/macintosh/.test(lower)) device = "Mac";
  else if (/windows/.test(lower)) device = "Windows";
  else if (/linux/.test(lower)) device = "Linux";
  let browser = "";
  if (/edg\//.test(lower)) browser = "Edge";
  else if (/chrome\//.test(lower)) browser = "Chrome";
  else if (/firefox\//.test(lower)) browser = "Firefox";
  else if (/safari\//.test(lower) && !/chrome/.test(lower)) browser = "Safari";
  return browser ? `${device} · ${browser}` : device;
}

function fmtAgo(iso) {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const future = t > Date.now();
  const diff = Math.abs(Date.now() - t) / 1000;
  if (diff < 60) return future ? "em segundos" : "agora mesmo";
  if (diff < 3600) {
    const m = Math.round(diff / 60);
    return future ? `em ${m} min` : `há ${m} min`;
  }
  if (diff < 86400) {
    const h = Math.round(diff / 3600);
    return future ? `em ${h} h` : `há ${h} h`;
  }
  const d = Math.round(diff / 86400);
  return future ? `em ${d} dias` : `há ${d} dias`;
}
